/**
 * lib/financialYear.ts
 * ------------------------------------------------------------------
 * CLOSING A MONTH, AND THE YEAR IT BELONGS TO.
 *
 * The posting engine has always refused to write into a closed period —
 * glWrite and poWrite both check it before every entry. Nothing could
 * close one. The lock was enforced and unreachable, which is a control
 * that exists on paper and nowhere else.
 *
 * A CLOSED MONTH IS A PROMISE TO EVERYBODY WHO READ IT. The moment a
 * figure leaves the building — a board pack, a management account, a set
 * of statements to the auditor — anything posted into that month
 * afterwards makes the copy in somebody's hand wrong, silently, with
 * nothing to say it changed. Closing is how the school says "this month
 * is now what it is".
 *
 * ABSENT MEANS OPEN. The collection holds only the months that have been
 * CLOSED. A school that has never closed anything must not find itself
 * unable to post; the lock is something a bursar applies, not a state
 * they have to escape.
 *
 * CLOSE IN ORDER. A closed March with an open February is not a control,
 * it is a gap: the February entries that turn up later change the opening
 * position of a March that was supposed to be settled. So a month can
 * only be closed once everything before it is.
 *
 * REOPENING IS ALLOWED AND RECORDED. Refusing it would guarantee that
 * the first genuine correction is made by editing history somewhere the
 * lock cannot see. What matters is that reopening leaves a mark.
 *
 * PURE. Periods and settings in, an answer out.
 * ------------------------------------------------------------------ */
import type { GlPeriod } from '@/lib/gl';

/**
 * The school's financial year runs January to December unless it says
 * otherwise. Stored rather than assumed: a school on an April year end
 * that finds its year hardcoded discovers it at year end, which is the
 * worst moment available.
 */
export interface FySettings {
  /** 1 = January. The month the financial year STARTS. */
  startMonth: number;
  /** How a year is named. 'start' → FY2026 for Apr 2026–Mar 2027. */
  labelBy: 'start' | 'end';
}

export const FY_DEFAULT: FySettings = { startMonth: 1, labelBy: 'start' };

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** '2026-08' → 'August 2026'. */
export function periodLabel(id: string): string {
  const [y, m] = id.split('-');
  const idx = Number(m) - 1;
  return `${MONTH_NAMES[idx] ?? m} ${y}`;
}

/** Which financial year a month belongs to, given where the year starts. */
export function fyOf(periodId: string, s: FySettings = FY_DEFAULT): string {
  const [ys, ms] = periodId.split('-');
  const year = Number(ys);
  const month = Number(ms);
  const startYear = month >= s.startMonth ? year : year - 1;
  return `FY${s.labelBy === 'start' ? startYear : startYear + 1}`;
}

/** Every month in a financial year, in order. */
export function periodsInFy(fy: string, s: FySettings = FY_DEFAULT): string[] {
  const n = Number(fy.replace(/\D/g, ''));
  const startYear = s.labelBy === 'start' ? n : n - 1;
  const out: string[] = [];
  for (let i = 0; i < 12; i += 1) {
    const m = ((s.startMonth - 1 + i) % 12) + 1;
    const y = startYear + Math.floor((s.startMonth - 1 + i) / 12);
    out.push(`${y}-${String(m).padStart(2, '0')}`);
  }
  return out;
}

export interface PeriodRow {
  id: string;
  label: string;
  fy: string;
  closed: boolean;
  closedAt?: string;
  closedByName?: string;
  note?: string;
  /** A month that has not happened yet cannot be closed. */
  future: boolean;
  /** Why this month cannot be closed right now, or null. */
  blocker: string | null;
}

/**
 * The months of a financial year with their state and what may be done.
 *
 * Computed together rather than per row, because "can this be closed"
 * depends on every earlier month — a question a single row cannot answer
 * about itself.
 */
export function buildPeriodRows(
  fy: string,
  closedPeriods: GlPeriod[],
  settings: FySettings = FY_DEFAULT,
  asOf: Date = new Date(),
): PeriodRow[] {
  const byId = new Map(closedPeriods.map((p) => [p.id, p]));
  const thisMonth = asOf.toISOString().slice(0, 7);

  const ids = periodsInFy(fy, settings);
  const rows: PeriodRow[] = [];

  for (const id of ids) {
    const rec = byId.get(id);
    const closed = Boolean(rec);
    const future = id > thisMonth;

    /* Everything before it, in calendar order — not just within this
       financial year, since December closes before the January that
       follows it whichever year they are filed under. */
    const earlierOpen = ids
      .filter((x) => x < id)
      .some((x) => !byId.has(x));

    let blocker: string | null = null;
    if (closed) blocker = null;
    else if (future) blocker = 'This month has not finished yet.';
    else if (earlierOpen) {
      blocker = 'An earlier month is still open. Close them in order, or a late entry '
        + 'into the earlier one changes the opening position of this one.';
    }

    rows.push({
      id,
      label: periodLabel(id),
      fy,
      closed,
      closedAt: rec?.closedAt,
      closedByName: rec?.closedByName,
      note: rec?.note,
      future,
      blocker,
    });
  }

  return rows;
}

/** Why this month cannot be closed, or null. */
export function closeProblem(row: PeriodRow): string | null {
  if (row.closed) return 'That month is already closed.';
  return row.blocker;
}

/**
 * Why this month cannot be reopened, or null.
 *
 * A LATER CLOSED MONTH BLOCKS IT. Reopening February under a closed
 * March means the entries that follow land in a month nobody will look
 * at again, and March's opening position quietly stops matching
 * February's close.
 */
export function reopenProblem(row: PeriodRow, rows: PeriodRow[]): string | null {
  if (!row.closed) return 'That month is not closed.';
  const laterClosed = rows.filter((r) => r.id > row.id && r.closed).map((r) => r.label);
  if (laterClosed.length) {
    return `${laterClosed[0]} is closed after it. Reopen the later months first, or an `
      + 'entry into this one changes an opening position nobody will check again.';
  }
  return null;
}

export interface FyProgress {
  total: number;
  closed: number;
  /** The earliest month still open, which is what somebody should do next. */
  nextToClose?: PeriodRow;
  /** True when every month that has finished is closed. */
  upToDate: boolean;
}

export function fyProgress(rows: PeriodRow[]): FyProgress {
  const due = rows.filter((r) => !r.future);
  const closed = rows.filter((r) => r.closed);
  const next = due.find((r) => !r.closed);
  return {
    total: rows.length,
    closed: closed.length,
    nextToClose: next,
    upToDate: due.length > 0 && due.every((r) => r.closed),
  };
}
